import React, { useEffect } from 'react';
import {
  Paper,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableFooter,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { useState } from 'react';
import { GenericTableHead, GenericTableRow } from './GenericTable.styled';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';

export interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
}

export interface GenericTableProps<T> {
  columns: Column[];
  rows: T[];
  loading?: boolean;
  error?: string | null;
  onRowClick?: (row: T) => void;
  getRowContent?: (row: T, columnId: string) => React.ReactNode;
  noDataMessage?: string;
  errorComponent?: React.ReactNode;
}

function GenericTable<T>({
  columns,
  rows = [],
  error,
  onRowClick,
  getRowContent,
  noDataMessage = 'No hay datos disponibles',
  errorComponent = null,
}: GenericTableProps<T>) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (rows.length !== 0) {
      setPage(0);
    }
  }, [rows]);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  /**
   * Lógica para cambiar de página
   * @param event
   * @param newPage
   */
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  /**
   * Lógica para cambiar el número de filas por página
   * @param event
   */
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (error) {
    return errorComponent ? (
      <>{errorComponent}</>
    ) : (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" component="div">
          {error}
        </Typography>
      </Paper>
    );
  }

  if (rows.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" component="div">
          {noDataMessage}
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'transparent' }}>
      <Table aria-label="generic table" sx={{ minWidth: 650, borderSpacing: '0px 5px', borderCollapse: 'separate' }}>
        <GenericTableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell key={column.id} align={column.align} sx={{ minWidth: column.minWidth }}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </GenericTableHead>
        <TableBody>
          {(rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows).map(
            (row, rowIndex) => (
              <GenericTableRow key={rowIndex} isClickable={!!onRowClick} onClick={() => onRowClick && onRowClick(row)}>
                {columns.map(column => (
                  <TableCell key={column.id} align={column.align}>
                    {getRowContent ? getRowContent(row, column.id) : row[column.id]}
                  </TableCell>
                ))}
              </GenericTableRow>
            )
          )}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={columns.length} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={columns.length} align="right">
              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 50, 100]}
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

export default GenericTable;
